import { useState } from 'react';
import { Button, Collapse, Group, JsonInput, Loader, Modal, Select, Stack, Table, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { requestRerun, rerunAnalysis, useAnalysisAction, type AnalysisStaff, type RerunBody, type Step } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useRun, useRunTypes, type RunType } from '@/api/runs';
import { ConfigForm, withDefaults, type ConfigValue, type JsonSchema } from '@/features/runs/ConfigForm';
import { brand } from '@/theme/theme';
import { advancedBody, configDiff, latestRunOfStep, NO_ADVANCED, rerunStartSteps, showValue, type AdvancedOptions, type RerunMode } from './rerun';

const NOTE_MAX = 4000;

interface Version {
  name: string;
  description: string;
  config_schema: JsonSchema;
}
const versionsOf = (rt: RunType | undefined): Version[] => (rt && Array.isArray(rt.versions) ? (rt.versions as Version[]) : []);

function schemaOf(rt: RunType | undefined, version: string | null): JsonSchema {
  const v = versionsOf(rt).find((x) => x.name === version);
  return v?.config_schema ?? ((rt?.config_schema as JsonSchema | undefined) ?? { type: 'object' });
}

/** Only the knobs the version's schema knows, when it allows no others: a version change can drop some. */
function fitTo(schema: JsonSchema, config: ConfigValue): ConfigValue {
  if (schema.additionalProperties !== false || !schema.properties) return config;
  return Object.fromEntries(Object.entries(config).filter(([k]) => k in schema.properties!));
}

export interface RerunModalProps {
  analysis: AnalysisStaff;
  steps: Step[];
  mode: RerunMode;
  onClose: () => void;
}

/**
 * Staff re-run (spec §3.4): the starting step by name, the run type's version against the last run's,
 * the configuration with what changes against the last run, a required note on why (kept as an
 * internal comment) and, folded away, forun's scheduling options.
 */
export function RerunModal({ analysis, steps, mode, onClose }: RerunModalProps) {
  const { t } = useTranslation('analyses');
  const { data: runTypes } = useRunTypes();
  const choices = rerunStartSteps(mode, steps.length, analysis.failed_step);
  const [step, setStep] = useState<number>(choices.at(-1) ?? 0);
  const rt = runTypes?.find((r) => r.key === steps[step]?.run_type);
  // What the server runs when no version is sent: the type's pin, else forun's default.
  const typeVersion = steps[step]?.version ?? rt?.default_version ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const version = picked ?? typeVersion;
  const lastRun = latestRunOfStep(analysis.runs ?? [], step);
  const lastDetail = useRun(lastRun?.public_id);
  const loading = !runTypes || (!!lastRun && !lastDetail.data);

  const stepLabel = (i: number) => {
    const failed = mode === 'failed' && i === analysis.failed_step;
    return `${i + 1} · ${steps[i]?.run_type ?? '?'}${failed ? ` · ${t('rerun.failedStep')}` : ''}`;
  };

  return (
    <Modal opened onClose={onClose} size="lg" title={mode === 'failed' ? t('actions.rerun') : t('actions.requestRerun')}>
      <Stack gap="md">
        <Select
          label={t('rerun.step')}
          description={mode === 'failed' ? t('rerun.stepHintFailed') : t('rerun.stepHintReview')}
          data={choices.map((i) => ({ value: String(i), label: stepLabel(i) }))}
          value={String(step)}
          onChange={(v) => {
            if (v === null) return;
            setStep(Number(v));
            setPicked(null);
          }}
          allowDeselect={false}
        />
        {rt && versionsOf(rt).length > 0 && (
          <Select
            label={t('rerun.version', { type: rt.key })}
            data={versionsOf(rt).map((v) => ({
              value: v.name,
              label: [v.name, v.name === rt.default_version ? t('rerun.defaultVersion') : null, v.description || null].filter(Boolean).join(' · '),
            }))}
            value={version}
            onChange={setPicked}
            allowDeselect={false}
          />
        )}
        {rt && version && <VersionLine type={rt.key} from={lastRun?.version ?? null} to={version} isDefault={version === rt.default_version} />}
        {loading ? (
          <Loader size="sm" color="forest" />
        ) : (
          <RerunForm
            key={`${step}|${version ?? ''}`}
            analysis={analysis}
            mode={mode}
            step={step}
            stepDef={steps[step]}
            schema={schemaOf(rt, version)}
            lastConfig={lastRun ? ((lastDetail.data?.config ?? {}) as ConfigValue) : null}
            version={version !== typeVersion ? version : null}
            onClose={onClose}
          />
        )}
      </Stack>
    </Modal>
  );
}

/** "odm 16 (last run) → 17 (current default)", or that the version stays. */
function VersionLine({ type, from, to, isDefault }: { type: string; from: string | null; to: string; isDefault: boolean }) {
  const { t } = useTranslation('analyses');
  const target = isDefault ? `${to} (${t('rerun.currentDefault')})` : to;
  const text = from === null ? t('rerun.versionFirst', { type, to: target }) : from === to ? t('rerun.versionSame', { type, version: target }) : t('rerun.versionChange', { type, from, to: target });
  return (
    <Text fz={13} fw={from !== null && from !== to ? 600 : undefined} c={from !== null && from !== to ? brand.text : brand.textMuted} data-testid="rerun-version">
      {text}
    </Text>
  );
}

interface RerunFormProps {
  analysis: AnalysisStaff;
  mode: RerunMode;
  step: number;
  stepDef: Step | undefined;
  schema: JsonSchema;
  /** The config the step's last run had; null when the step never ran. */
  lastConfig: ConfigValue | null;
  /** A version to pin, only when it differs from what the server would pick anyway. */
  version: string | null;
  onClose: () => void;
}

/** Remounted per step and version, so the form starts from that step's last run in that version's schema. */
function RerunForm({ analysis, mode, step, stepDef, schema, lastConfig, version, onClose }: RerunFormProps) {
  const { t } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const qc = useQueryClient();
  const typeConfig = withDefaults(schema, { ...((stepDef?.config ?? {}) as ConfigValue), ...(((analysis.config ?? {}) as Record<string, ConfigValue>)[String(step)] ?? {}) });
  const base = lastConfig ?? typeConfig;
  const [config, setConfig] = useState<ConfigValue>(() => withDefaults(schema, fitTo(schema, base)));
  const [note, setNote] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedOptions>(NO_ADVANCED);
  const [error, setError] = useState<string | null>(null);
  const rerun = useAnalysisAction(rerunAnalysis);
  const review = useAnalysisAction(requestRerun);
  const changes = configDiff(base, config);

  const go = async () => {
    setError(null);
    let scheduling: ReturnType<typeof advancedBody>;
    try {
      scheduling = advancedBody(advanced);
    } catch {
      setAdvancedOpen(true);
      setError(t('rerun.resourcesInvalid'));
      return;
    }
    const body: RerunBody = { from_step: step, config_overrides: config as Record<string, never>, note: note.trim(), ...scheduling, ...(version ? { version } : {}) };
    try {
      if (mode === 'failed') await rerun.mutateAsync({ id: analysis.public_id, args: [body] });
      else await review.mutateAsync({ id: analysis.public_id, args: [body] });
      void qc.invalidateQueries({ queryKey: ['comments', analysis.public_id] });
      void qc.invalidateQueries({ queryKey: ['comment-counts'] });
      onClose();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Stack gap="md">
      <Stack gap={4} data-testid="rerun-diff">
        <Text fz={13} fw={600}>
          {lastConfig ? t('rerun.diffVsLast') : t('rerun.diffVsType')}
        </Text>
        {changes.length === 0 ? (
          <Text fz={13} c={brand.textMuted}>
            {t('rerun.noChanges')}
          </Text>
        ) : (
          <Table fz={13} withRowBorders={false} verticalSpacing={2}>
            <Table.Tbody>
              {changes.map((c) => (
                <Table.Tr key={c.key}>
                  <Table.Td ff="monospace">{c.key}</Table.Td>
                  <Table.Td ff="monospace" c={brand.textMuted}>
                    {showValue(c.before, t('rerun.unset'))}
                  </Table.Td>
                  <Table.Td>→</Table.Td>
                  <Table.Td ff="monospace" fw={600}>
                    {showValue(c.after, t('rerun.unset'))}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <Text fz={13} c={brand.textMuted}>
        {t('actions.overridesHint', { type: stepDef?.run_type })}
      </Text>
      <ConfigForm schema={schema} value={config} onChange={setConfig} />
      <Textarea
        label={t('rerun.note')}
        description={t('rerun.noteHint')}
        required
        autosize
        minRows={2}
        maxLength={NOTE_MAX}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
      />
      <Stack gap="xs">
        <Group>
          <Button variant="subtle" size="compact-sm" onClick={() => setAdvancedOpen((o) => !o)} aria-expanded={advancedOpen}>
            {advancedOpen ? t('rerun.advancedHide') : t('rerun.advanced')}
          </Button>
        </Group>
        <Collapse expanded={advancedOpen}>
          <Stack gap="sm">
            <Group grow>
              <Select
                label={t('rerun.priority')}
                data={[
                  { value: '', label: t('rerun.forunDefault') },
                  { value: 'normal', label: t('rerun.priorityNormal') },
                  { value: 'low', label: t('rerun.priorityLow') },
                ]}
                value={advanced.priority}
                onChange={(v) => setAdvanced((a) => ({ ...a, priority: (v ?? '') as AdvancedOptions['priority'] }))}
                allowDeselect={false}
              />
              <Select
                label={t('rerun.overflow')}
                data={[
                  { value: '', label: t('rerun.forunDefault') },
                  { value: 'allow', label: t('rerun.overflowAllow') },
                  { value: 'pool', label: t('rerun.overflowPool') },
                ]}
                value={advanced.overflow}
                onChange={(v) => setAdvanced((a) => ({ ...a, overflow: (v ?? '') as AdvancedOptions['overflow'] }))}
                allowDeselect={false}
              />
            </Group>
            <JsonInput
              label={t('rerun.resources')}
              description={t('rerun.resourcesHint')}
              placeholder='{"odm": {"memory_gib": 256}}'
              autosize
              minRows={2}
              value={advanced.resources}
              onChange={(v) => setAdvanced((a) => ({ ...a, resources: v }))}
            />
          </Stack>
        </Collapse>
      </Stack>
      {error && (
        <Text c="red" role="alert" fz={14}>
          {error}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {tc('common.cancel')}
        </Button>
        <Button loading={rerun.isPending || review.isPending} disabled={!note.trim()} onClick={() => void go()}>
          {t('actions.start')}
        </Button>
      </Group>
    </Stack>
  );
}
